import { baseApi, QueryTag, type ApiCollectionResponse, type ApiResponse } from 'shared/api';
import {
  apiV1SubscriptionsSummaryPath,
  apiV1TrackedSubjectSubjectChangeEventGroupsPath,
  apiV1TrackedSubjectSubjectChangeEventPath,
  apiV1TrackedSubjectSubjectChangeEventsPath,
  markAllReadApiV1TrackedSubjectSubjectChangeEventsPath,
  markSubjectReadApiV1TrackedSubjectSubjectChangeEventGroupsPath,
  subjectChangeEventGroupsApiV1TrackedSubjectsPath,
  subjectChangeEventGroupsMarkSubjectReadApiV1TrackedSubjectsPath,
  subjectChangeEventsApiV1TrackedSubjectsPath,
  subjectChangeEventsMarkAllReadApiV1TrackedSubjectsPath,
} from 'shared/api/routes';

import type {
  ISubjectChangeEvent,
  ISubjectChangeEventsQueryParams,
  ISubjectNewsGroup,
  ISubjectNewsQueryParams,
  ISubscriptionSummary,
} from '../model/types';

const subjectChangeEventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    allSubjectNews: builder.query<ApiCollectionResponse<ISubjectNewsGroup>, ISubjectNewsQueryParams | void>({
      query: (params) => ({
        url: subjectChangeEventGroupsApiV1TrackedSubjectsPath(),
        method: 'GET',
        params,
      }),
      providesTags: [QueryTag.SubjectChangeEvent, QueryTag.TrackedSubject, QueryTag.SubscriptionSummary],
    }),

    subjectNews: builder.query<
      ApiCollectionResponse<ISubjectNewsGroup>,
      { trackedSubjectId: number; params?: ISubjectNewsQueryParams }
    >({
      query: ({ trackedSubjectId, params }) => ({
        url: apiV1TrackedSubjectSubjectChangeEventGroupsPath(trackedSubjectId),
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { trackedSubjectId }) => [
        { type: QueryTag.SubjectChangeEvent, id: trackedSubjectId },
        QueryTag.TrackedSubject,
        QueryTag.SubscriptionSummary,
      ],
    }),

    allSubjectChangeEvents: builder.query<
      ApiCollectionResponse<ISubjectChangeEvent>,
      ISubjectChangeEventsQueryParams | void
    >({
      query: (params) => ({
        url: subjectChangeEventsApiV1TrackedSubjectsPath(),
        method: 'GET',
        params,
      }),
      providesTags: [QueryTag.SubjectChangeEvent, QueryTag.TrackedSubject, QueryTag.SubscriptionSummary],
    }),

    subjectChangeEvents: builder.query<
      ApiCollectionResponse<ISubjectChangeEvent>,
      { trackedSubjectId: number; params?: ISubjectChangeEventsQueryParams }
    >({
      query: ({ trackedSubjectId, params }) => ({
        url: apiV1TrackedSubjectSubjectChangeEventsPath(trackedSubjectId),
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { trackedSubjectId }) => [
        { type: QueryTag.SubjectChangeEvent, id: trackedSubjectId },
        QueryTag.TrackedSubject,
        QueryTag.SubscriptionSummary,
      ],
    }),

    markSubjectNewsRead: builder.mutation<void, { subjectType: string; subjectId: number; trackedSubjectId?: number }>({
      query: ({ subjectType, subjectId, trackedSubjectId }) => {
        const data = { subjectType, subjectId };

        if (trackedSubjectId != null) {
          return {
            url: markSubjectReadApiV1TrackedSubjectSubjectChangeEventGroupsPath(trackedSubjectId),
            method: 'POST',
            data,
          };
        }

        return {
          url: subjectChangeEventGroupsMarkSubjectReadApiV1TrackedSubjectsPath(),
          method: 'POST',
          data,
        };
      },
      invalidatesTags: (_result, _error, { trackedSubjectId }) => [
        ...(trackedSubjectId != null ? [{ type: QueryTag.SubjectChangeEvent, id: trackedSubjectId } as const] : []),
        QueryTag.SubjectChangeEvent,
        QueryTag.TrackedSubject,
        QueryTag.SubscriptionSummary,
      ],
    }),

    markSubjectChangeEventRead: builder.mutation<
      ApiResponse<ISubjectChangeEvent>,
      { trackedSubjectId: number; eventId: number }
    >({
      query: ({ trackedSubjectId, eventId }) => ({
        url: apiV1TrackedSubjectSubjectChangeEventPath(trackedSubjectId, eventId),
        method: 'PATCH',
        data: { subjectChangeEvent: { state: 'readed' } },
      }),
      invalidatesTags: (_result, _error, { trackedSubjectId }) => [
        { type: QueryTag.SubjectChangeEvent, id: trackedSubjectId },
        QueryTag.SubjectChangeEvent,
        QueryTag.TrackedSubject,
        QueryTag.SubscriptionSummary,
      ],
    }),

    markAllSubjectChangeEventsRead: builder.mutation<void, { trackedSubjectId?: number } | void>({
      query: (params) => {
        if (params?.trackedSubjectId != null) {
          return {
            url: markAllReadApiV1TrackedSubjectSubjectChangeEventsPath(params.trackedSubjectId),
            method: 'POST',
          };
        }

        return {
          url: subjectChangeEventsMarkAllReadApiV1TrackedSubjectsPath(),
          method: 'POST',
        };
      },
      invalidatesTags: (_result, _error, params) => [
        ...(params?.trackedSubjectId != null
          ? [{ type: QueryTag.SubjectChangeEvent, id: params.trackedSubjectId } as const]
          : []),
        QueryTag.SubjectChangeEvent,
        QueryTag.TrackedSubject,
        QueryTag.SubscriptionSummary,
      ],
    }),

    subscriptionSummary: builder.query<ISubscriptionSummary, void>({
      query: () => ({
        url: apiV1SubscriptionsSummaryPath(),
        method: 'GET',
      }),
      providesTags: [QueryTag.SubscriptionSummary],
    }),
  }),
});

export const {
  useAllSubjectNewsQuery,
  useSubjectNewsQuery,
  useAllSubjectChangeEventsQuery,
  useSubjectChangeEventsQuery,
  useMarkSubjectNewsReadMutation,
  useMarkSubjectChangeEventReadMutation,
  useMarkAllSubjectChangeEventsReadMutation,
  useSubscriptionSummaryQuery,
} = subjectChangeEventsApi;
